import { HttpClient } from '@angular/common/http';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Event, NavigationStart, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { devLog } from 'src/app/core/utils/generic-utils';
import { getQueryParameters } from 'src/app/core/utils/global-utils';
import { environment } from 'src/environments/environment';
import { ShopRedirectService } from '../../services/shop-redirect.service';
import shortHash from 'short-hash';
import { desktopViewWidth, trackingSalt } from 'src/app/core/constants/constants';
import { HubUser } from 'src/app/core/models/user-data';
import { tokenClaimsField } from '../../utils/secured-skeleton-utils';
import { SecuredDynamicLoadingService } from 'src/app/core/services/secured-dynamic-loading.service';
import { CustomerPortal } from '../../models/customer-portal';
import { CustomerPortalSubmenu } from '../../models/customer-portal-submenu';
import { DataService } from 'src/app/core/services/data.service';
import { ThreadMessengerService } from '../../services/thread-messenger.service';
import { FeaturesFlagsV2Service } from 'src/app/core/services/features-flags-v2.service';
import { UserSessionService } from 'src/app/core/services/user-session.service';
import { PERMISSION } from 'src/app/core/constants/permissions';

@Component({
  selector: 'app-secured-skeleton',
  templateUrl: './secured-skeleton.component.html',
  styleUrls: ['./secured-skeleton.component.scss'],
  standalone: false,
})
export class SecuredSkeletonComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  private routerSubscription: Subscription | undefined;

  private shopRedirectSubscription: Subscription | undefined;

  readonly now = new Date();

  /** True if the current desktop view is greater than [desktopViewWidth]*/
  isDesktopView = window.innerWidth > desktopViewWidth;

  events: string[] = [];

  opened: boolean;

  get isCreatingRedirectToken(): boolean {
    return this.shopRedirectService.isCreatingRedirectToken;
  }

  /** True while the logout is in progress */
  isLoggingOut: boolean = false;

  // isRpsActive: boolean = false;

  isOtActive: boolean = false;

  // currentModule: string = '';

  /** TODO: Specify app types */
  customerApps: CustomerPortal[] = [];

  /** True if the user data has been successfully loaded */
  get hasLoadedData(): boolean {
    return this.userDataService.user !== null && !this.userDataService.isLoadingCustomerInfo;
  }

  /** True if the order tracking route is active */
  get isOrderTrackingActive(): boolean {
    return this.router.url.includes('secured/orders');
  }

  get isWarrantyActive(): boolean {
    return this.router.url.includes('/secured/support');
  }

  get disableShop(): boolean {
    return this.userDataService.company.name === 'Hernando School District';
  }

  get isRpsActive(): boolean {
    return this.router.url.includes(`/secured/support`);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: UIEvent): void {
    const newIsDesktopView = window.innerWidth > desktopViewWidth;
    if (newIsDesktopView !== this.isDesktopView) {
      this.checkSidenavDisplay();
    }
    this.isDesktopView = newIsDesktopView;
  }

  /** TODO: Migrate the customer apps service logic, inside the user data service */
  constructor(
    private authService: MsalService,
    private http: HttpClient,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userDataService: UserDataService,
    private msalBroadcastService: MsalBroadcastService,
    private userSessionService: UserSessionService,
    private shopRedirectService: ShopRedirectService,
    public dynamicLoadingService: SecuredDynamicLoadingService,
    private dataService: DataService,
    private threadMessengerService: ThreadMessengerService,
    private featuresFlagsV2Service: FeaturesFlagsV2Service,
    private permissionsService: PermissionsService,
  ) {}

  ngOnInit() {
    this.featuresFlagsV2Service.getFeaturesFlags();

    this.userDataService.fetchCustomerInfo((userData, portalsData) => {
      this.registerRouteListener();
      const hasCrossAccountPermissions = (userData.user.crossAccounts?.length ?? 0) > 1;
      this.checkTrackingParamsAndNavigate(
        this.router,
        this.activatedRoute,
        this.router.url,
        userData.company.name,
        userData.user.email,
        hasCrossAccountPermissions,
      );
      this.updateUserFullNameIfNeeded(userData.user);
      this.updateUserHashIfNeeded(userData.user);

      const availablePortals = portalsData.filter(
        (portal) =>
          portal.isActive &&
          portal.hasAccess &&
          // Hide the entire portal tab when the user has no access to its addon.
          this.permissionsService.hasAddonAccess(portal.addonName),
      );

      this.customerApps = this.filterPortalSubmenusByPermission(availablePortals);
    });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.Logout),
        takeUntil(this._destroying$),
      )
      .subscribe((_) => {
        this.isLoggingOut = true;
      });

    // Note the place where this function is called could be improved, but for the moment this place is enough
    this.http.post(`${environment.restUrl}/v1/hub/register-login`, {}).subscribe();
  }

  ngOnDestroy() {
    this.threadMessengerService.destroy();

    this.shopRedirectSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    this.routerSubscription = undefined;
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  // MARK: Public methods

  logOut() {
    this.threadMessengerService.destroy();
    this.userSessionService.logout();
  }

  ngAfterViewChecked(): void {
    this.checkSidenavDisplay();
  }

  // Cosmetic mismatches between submenu.name from /v1/hub/apps and the canonical
  // submodule key used in V2 permissions and V1 SUBMODULE constants. Until the
  // backend aligns these, normalize here so the lookup matches.
  private readonly API_SUBMENU_TO_SUBMODULE: Record<string, string> = {
    'Dashboards (BETA)': 'Dashboards',
    'Inventory Stock': 'Stock Inventory',
  };

  // Hide submenus the current user can't view. Uses each submenu's own name as the
  // submodule key when calling hasPermission — works generically for every addon
  // because the backend keeps submenu.name aligned with the V2 submodule keys
  // (with a few cosmetic exceptions handled via API_SUBMENU_TO_SUBMODULE).
  private filterPortalSubmenusByPermission(portals: CustomerPortal[]): CustomerPortal[] {
    return portals.map((portal) => {
      if (!portal.submenus?.length) return portal;
      return {
        ...portal,
        submenus: this.filterSubmenusRecursively(portal.submenus, portal.addonName),
      };
    });
  }

  // Recursively filters submenus by permission. Two reasons to drop a submenu:
  //   1) it's a leaf and the user lacks `view` on its submodule
  //   2) it's a container (has children) whose children all got filtered out
  private filterSubmenusRecursively(submenus: CustomerPortalSubmenu[], addonName: string): CustomerPortalSubmenu[] {
    const result: CustomerPortalSubmenu[] = [];

    for (const submenu of submenus) {
      const hadChildren = (submenu.submenus?.length ?? 0) > 0;
      const filteredChildren = hadChildren
        ? this.filterSubmenusRecursively(submenu.submenus!, addonName)
        : submenu.submenus;

      // Containers group children and don't get their own permission check — they
      // can have a URI (e.g. Stock, Settings, Logs in lifecycle-command) that is
      // just a path prefix, not a real destination. Visibility is decided by the
      // empty-container rule below.
      if (!hadChildren) {
        const submoduleName = this.API_SUBMENU_TO_SUBMODULE[submenu.name] ?? submenu.name;
        if (!this.permissionsService.hasPermission(addonName, submoduleName, PERMISSION.view)) {
          continue;
        }
      }

      if (hadChildren && (filteredChildren?.length ?? 0) === 0) {
        continue;
      }

      result.push({ ...submenu, submenus: filteredChildren });
    }

    return result;
  }

  private registerRouteListener() {
    if (this.routerSubscription !== undefined) {
      return;
    }

    this.routerSubscription = this.router.events
      .pipe(filter((routerEvent: Event) => routerEvent instanceof NavigationStart))
      .subscribe((routerEvent) => {
        if (this.userDataService.company === undefined || this.userDataService.user === undefined) {
          console.warn(`Undefined company or user, in router event`);
          return;
        }

        const hasCrossAccountPermissions = (this.userDataService.user?.crossAccounts?.length ?? 0) > 1;
        this.checkTrackingParamsAndNavigate(
          this.router,
          this.activatedRoute,
          (routerEvent as NavigationStart).url,
          this.userDataService.company.name,
          this.userDataService.user.email,
          hasCrossAccountPermissions,
        );
      });
  }

  /**
   * It set the state of the sidenav to be collapsed or expanded
   */
  private checkSidenavDisplay(): void {
    if (this.isDesktopView) {
      if (this.dataService.isCollapsedSidenav) {
        this.dataService.setExpandedSidenavClosedState(false);
      }
      this.dataService.setCollapsedSidenavState(false);
    } else {
      //Set the sidenav to be closed
      this.dataService.setCollapsedSidenavState(true);
      this.dataService.setExpandedSidenavClosedState(true);
    }
  }

  /** @pure Given it is called through callback */
  private checkTrackingParamsAndNavigate(
    router: Router,
    activatedRoute: ActivatedRoute,
    url: string,
    company: string,
    email: string,
    hasCrossAccountPermissions: boolean,
  ) {
    const queryParams = getQueryParameters(url);
    const { k, v } = queryParams;

    // No need to add tracking parameters, however this might fail if the URL comes with hashes
    if (hasCrossAccountPermissions) {
      if (k !== undefined && v !== undefined) {
        devLog(`Removing query parameters A`);
        router.navigate([], {
          relativeTo: activatedRoute,
          queryParams: {
            k: null,
            v: null,
          },
          queryParamsHandling: 'merge',
        });
      }

      return;
    }

    const companyHash: string = this.hashWithSalt(company);
    const userHash: string = this.hashWithSalt(email);

    if (k !== companyHash || v !== userHash) {
      devLog(`Adding query parameters B`);
      //remove query params of url
      const urlWithoutQueryParams = url.split('?')[0];
      queryParams['k'] = companyHash;
      queryParams['v'] = userHash;
      router.navigate([urlWithoutQueryParams], {
        queryParams,
      });
    }
  }

  /** Returns the result of applying the [shortHash] with the [trackingSalt] to the given [input] */
  private hashWithSalt(input: string): string {
    return shortHash(`${input}${trackingSalt}`);
  }

  /**
   * Updates the name of the given [user] in the DB, from the data set in Azure B2C
   * If the user's name is the same as the one in Azure B2C, no action is taken
   */
  private updateUserFullNameIfNeeded(user: HubUser) {
    const dbName = (user.fullName ?? '').trim();
    const b2cName = (tokenClaimsField('name', this.authService) ?? '').trim();
    if (dbName === '' && dbName !== b2cName) {
      // Second condition should never be false if the first one is true
      // devLog(`Updating user full name: ${b2cName}`)
      this.http
        .patch(`${environment.restUrl}/v1/users/${user.id}`, {
          fullName: b2cName,
        })
        .subscribe();
    }
  }

  /**
   * Updates the tracking associated to the given [user]
   * If the user already has the same hash as the one computed here, no action is taken
   */
  private updateUserHashIfNeeded(user: HubUser) {
    const dbHash = user.trackingHash;
    const userHash = this.hashWithSalt(user.email);

    if (userHash !== dbHash) {
      // devLog(`Updating user tracking hash`)
      this.http.put(`${environment.restUrl}/v1/users/${user.id}/trackingHash`, {}).subscribe();
    }
  }

  onCustomerPortalClicked({ app }: { app: CustomerPortal }) {
    if (!this.userDataService.company.serviceAddons.includes(app.addonName)) {
      this.router.navigateByUrl(`/secured/cross-selling/${app.addonName}`);
      return;
    } else if (!this.permissionsService.hasAddonAccess(app.addonName)) {
      this.router.navigateByUrl(`/secured/forbidden/${app.addonName}`);
      return;
    }

    if (app.name === `Shop`) {
      // shop is an exception

      this.shopRedirectService.xchangeClicked();
    } else if (app.isExternal) {
      if (app.sections.length > 0) {
        const fullUrl = this.createPath(app.baseUrl, app.sections[0].subdomain, app.sections[0].uri);
        window.open(fullUrl, '_blank');
      } else {
        window.open(app.baseUrl, '_blank');
      }
    } else {
      if (app.addonName === 'managed-services' || app.addonName === 'connectivity-services') {
        this.router.navigateByUrl(app.baseUrl);
      } else {
        const fullUrl = this.createPath(app.baseUrl, app.sections[0].subdomain, app.sections[0].uri);
        this.router.navigateByUrl(fullUrl);
      }
    }
  }

  onCustomerPortalKeydown({ $event, app }: { $event: KeyboardEvent; app: CustomerPortal }) {
    if ($event.key === 'Enter' || $event.key === ' ') {
      this.onCustomerPortalClicked({ app });
    }
  }

  createPath(baseUrl: string, subdomain: string, uri: string): string {
    let path = baseUrl;

    /** If subdomain has a value append this at the beginning  */
    if (subdomain) {
      path = baseUrl.replace('https://', `https://${subdomain}.`);
    }

    /** If uri has a value append this at the end */
    if (uri) {
      path += uri;
    }

    return path;
  }
}
